/**
 * CartaoDoAtendimento — D3 (passo 19, PR 2), peça H do desenho do portal (§3.3).
 *
 * O miolo do aviso na tela quando TODOS os itens do grupo são respostas do atendente da MESMA sessão. Irmão SIMPLES do
 * `ConteudoDoAviso`: sem foto (o parceiro só recebe o primeiro nome do atendente), sem "Lida" e sem "Ver todas" — a resposta
 * não é linha do sino (§8.1, decisão 4); ler é abrir a conversa. Só o corpo (botão esticado ATRÁS do conteúdo: botão dentro de
 * botão é HTML inválido) e o X.
 *
 * O título é a MESMA linha do cabeçalho da conversa (`descricaoDaSessao`): o parceiro reconhece. Rajada da mesma conversa com o
 * aviso aberto → o `update()` do `AvisoNaTela` traz mais um item e o rodapé vira "Atendimento · N respostas novas".
 * 🔴 NUNCA usa API interna (`useColegasInternos`, `ChatRoomAvatar`): um 401 interno apagaria o `seweb_auth` (mapa A5).
 */
import { MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ToastClose, ToastDescription, ToastTitle } from "@/components/ui/toast";
import { descricaoDaSessao } from "@/lib/sessaoDisplay";
import type { ItemDeAtendimento } from "./AvisoNaTela";
import { quandoDoAviso } from "./auxiliaresDoAviso";

export interface CartaoDoAtendimentoProps {
  itens: ItemDeAtendimento[];
  /** O destino JÁ validado pelo app (`caminhoSeguro`); `null` se o link do evento não passou. */
  destino: string | null;
  /** Para onde o corpo leva quando não há destino (a inbox do portal). */
  alternativo: string;
  aoFechar: () => void;
}

export function CartaoDoAtendimento({
  itens,
  destino,
  alternativo,
  aoFechar,
}: CartaoDoAtendimentoProps) {
  const navigate = useNavigate();
  const atual = itens[itens.length - 1];
  const { session_id, setor, author_name } = atual.atendimento;
  const titulo = descricaoDaSessao({
    id: session_id,
    sector_name: setor,
    status: "IN_PROGRESS",
    attendant_first_name: author_name,
  });
  const rodape =
    itens.length > 1
      ? `Atendimento · ${itens.length} respostas novas`
      : `Atendimento · ${quandoDoAviso(atual.created_at)}`;

  const abrir = () => {
    navigate(destino ?? alternativo);
    aoFechar();
  };

  return (
    <div
      className="group/aviso relative flex w-full gap-3 p-4 pr-10"
      data-testid="aviso-atendimento"
    >
      {/* Corpo clicável = botão esticado ATRÁS do conteúdo; o X fica por cima. */}
      <button
        type="button"
        aria-label={`Abrir: ${titulo}`}
        onClick={abrir}
        className="absolute inset-0 z-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary [&_svg]:h-4 [&_svg]:w-4"
      >
        <MessageSquare />
      </span>
      <div className="pointer-events-none relative min-w-0 flex-1">
        <ToastTitle className="truncate leading-5 group-hover/aviso:underline">{titulo}</ToastTitle>
        {atual.body && (
          <ToastDescription className="line-clamp-2 leading-5 text-foreground/80">
            {atual.body}
          </ToastDescription>
        )}
        {/* Sem flex-wrap (cicatriz do flex-wrap no desktop): o rótulo trunca, o "Abrir ›" não encolhe. */}
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="min-w-0 truncate">{rodape}</span>
          <span aria-hidden="true" className="ml-auto shrink-0 text-xs font-medium text-primary">
            Abrir ›
          </span>
        </div>
      </div>
      <ToastClose aria-label="Fechar aviso" className="z-10 opacity-100 sm:opacity-0" />
    </div>
  );
}
